/**
 * Screenshot Capture Utility (Issue #3646)
 *
 * Uses the native Screen Capture API (getDisplayMedia) for reliable
 * screenshot capture. The browser handles rendering — oklch colors,
 * fonts, Safari quirks — all work because it's real pixel capture,
 * not DOM serialization.
 *
 * html-to-image and html2canvas were removed after 3+ production
 * failures with Tailwind v4 CSS. See the screenshot-architecture deliberation
 * in SchoolAI/cadence-reference (was docs/deliberations/screenshot-architecture/,
 * moved in #11745)
 *
 * Capture is user-initiated: the user clicks a button, the browser
 * shows its native tab/window picker, and one frame is captured.
 *
 * There is no fallback. When getDisplayMedia is unsupported or fails, the
 * feedback simply has no screenshot; file upload and paste still attach
 * images. The server-side Playwright fallback (#3659) was removed in #14858:
 * it re-rendered a different page than the user saw and kept a headless
 * Chromium in the web image. Historical `feedback.captureMetadata` rows may
 * still carry `method: 'server-side'`; readers must tolerate it.
 */

// Size limits for feedback screenshots
export const MAX_SCREENSHOT_SIZE_BYTES = 1 * 1024 * 1024 // 1MB

export interface ScreenshotResult {
  blob: Blob
  dataUrl: string
  width: number
  height: number
}

/** Capture methods tracked for analytics (Issue #3646) */
export type CaptureMethod =
  | 'getDisplayMedia'
  | 'file-upload'
  | 'clipboard-paste'

/** Metadata for a single capture attempt (Issue #3646) */
export interface CaptureAttempt {
  method: CaptureMethod
  outcome: 'success' | 'failure' | 'cancelled' | 'unsupported'
  durationMs: number
  error: string | null
  sizeBytes: number | null
  pageUrl: string
}

/** Result of captureScreenshot including attempt metadata */
export interface CaptureResult {
  screenshot: ScreenshotResult | null
  attempt: CaptureAttempt
}

/**
 * Check whether getDisplayMedia is available in this browser.
 * Returns false on mobile browsers and older desktop browsers.
 */
export function isScreenCaptureSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices !== 'undefined' &&
    typeof navigator.mediaDevices.getDisplayMedia === 'function'
  )
}

/**
 * Safely extract an error message from an unknown thrown value.
 */
function formatError(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  return String(error) || 'Unknown error'
}

/**
 * Capture a screenshot using the native Screen Capture API.
 *
 * The browser shows its native tab/window picker. The user
 * selects a surface. One video frame is captured, drawn to
 * a canvas, and converted to a PNG blob.
 *
 * Returns null screenshot if the user cancels or the API
 * is unsupported. Always returns attempt metadata.
 *
 * @returns Screenshot result (or null) plus capture attempt metadata
 */
export async function captureScreenshot(): Promise<CaptureResult> {
  const startTime = performance.now()
  const pageUrl = window.location.pathname

  if (!isScreenCaptureSupported()) {
    return {
      screenshot: null,
      attempt: {
        method: 'getDisplayMedia',
        outcome: 'unsupported',
        durationMs: 0,
        error: 'getDisplayMedia not supported',
        sizeBytes: null,
        pageUrl,
      },
    }
  }

  try {
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: { displaySurface: 'browser' },
    })

    // Grab a single frame from the video stream
    const video = document.createElement('video')
    video.srcObject = stream
    await video.play()

    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    canvas.getContext('2d')!.drawImage(video, 0, 0)

    // Stop all tracks immediately — we only need one frame
    stream.getTracks().forEach(t => t.stop())

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        b => (b ? resolve(b) : reject(new Error('toBlob failed'))),
        'image/png',
      )
    })

    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = () => reject(new Error('FileReader failed'))
      reader.readAsDataURL(blob)
    })

    const durationMs = Math.round(performance.now() - startTime)
    console.info('[Screenshot] Capture succeeded', {
      method: 'getDisplayMedia',
      pageUrl,
      durationMs,
      sizeBytes: blob.size,
      width: canvas.width,
      height: canvas.height,
    })

    return {
      screenshot: {
        blob,
        dataUrl,
        width: canvas.width,
        height: canvas.height,
      },
      attempt: {
        method: 'getDisplayMedia',
        outcome: 'success',
        durationMs,
        error: null,
        sizeBytes: blob.size,
        pageUrl,
      },
    }
  } catch (error) {
    const durationMs = Math.round(performance.now() - startTime)
    const errorMsg = formatError(error)

    // User clicking "Cancel" in the permission dialog throws
    // NotAllowedError — this is not a failure, it's a cancellation
    const isCancelled =
      error instanceof DOMException && error.name === 'NotAllowedError'

    console.info(
      '[Screenshot] Capture ' + (isCancelled ? 'cancelled' : 'failed'),
      {
        method: 'getDisplayMedia',
        pageUrl,
        durationMs,
        error: errorMsg,
      },
    )

    return {
      screenshot: null,
      attempt: {
        method: 'getDisplayMedia',
        outcome: isCancelled ? 'cancelled' : 'failure',
        durationMs,
        error: errorMsg,
        sizeBytes: null,
        pageUrl,
      },
    }
  }
}

/**
 * Check if a screenshot blob is within acceptable size limits
 */
export function isScreenshotSizeValid(blob: Blob): boolean {
  return blob.size <= MAX_SCREENSHOT_SIZE_BYTES
}
